<template>
  <div class="step-birth">
    <!-- Loading state after submit -->
    <div v-if="submitting" class="loading-screen">
      <div class="loader-ring">
        <svg viewBox="0 0 100 100">
          <circle class="ring-bg" cx="50" cy="50" r="42" />
          <circle class="ring-fg" cx="50" cy="50" r="42" :stroke-dasharray="progress * 2.64 + ', 264'" />
        </svg>
        <span class="ring-pct">{{ Math.round(progress) }}%</span>
      </div>
      <p class="loading-text">Aligning your celestial blueprint...</p>
    </div>

    <!-- Form -->
    <template v-else>
    <h2>Your Birth Details</h2>
    <form @submit.prevent="submit" class="birth-form">
      <div class="form-group">
        <label>Name</label>
        <input v-model="form.name" type="text" placeholder="Your name" required>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label>Birth Date</label>
          <input v-model="form.birthDate" type="date" min="1920-01-01" max="2025-12-31" required>
        </div>
        <div class="form-group">
          <label>Birth Time</label>
          <input v-model="form.birthTime" type="time" required>
        </div>
      </div>
      <div class="form-group">
        <label>Birth Place</label>
        <location-search @location-selected="onLocationSelected" />
      </div>
      <div class="form-group">
        <label>Gender</label>
        <div class="gender-options">
          <label v-for="g in genders" :key="g.value" class="gender-option" :class="{ active: form.gender === g.value }">
            <input type="radio" v-model="form.gender" :value="g.value" class="sr-only">
            <span>{{ g.label }}</span>
          </label>
        </div>
      </div>
      <button type="submit" class="primary-btn" :disabled="!isValid || submitting">Continue</button>
    </form>
    </template>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { signInAnonymously } from 'firebase/auth'
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { initFirebase } from '../../firebase'
import LocationSearch from '../LocationSearch.vue'

const { db, auth, functions } = initFirebase()


export default {
  name: 'StepBirthInfo',
  components: { LocationSearch },
  emits: ['next', 'summary-complete'],
  setup(_, { emit }) {
    const form = ref({
      name: '',
      birthDate: '',
      birthTime: '',
      gender: '',
      location: null
    })
    const submitting = ref(false)
    const progress = ref(0)
    let progressTimer = null
    const genders = [
      { value: 'female', label: 'Female' },
      { value: 'male', label: 'Male' },
      { value: 'non-binary', label: 'Non-binary' },
      { value: 'prefer_not_to_say', label: 'Prefer not to say' }
    ]

    const isValid = computed(() => {
      return form.value.name && form.value.birthDate && form.value.birthTime && form.value.location && form.value.gender
    })

    const onLocationSelected = (loc) => {
      form.value.location = loc
    }

    const startProgress = () => {
      progress.value = 0
      progressTimer = setInterval(() => {
        if (progress.value < 70) {
          progress.value += 2
        } else if (progress.value < 95) {
          progress.value += 0.3
        }
      }, 100)
    }

    const finishProgress = () => {
      return new Promise(resolve => {
        clearInterval(progressTimer)
        const finish = setInterval(() => {
          progress.value += 3
          if (progress.value >= 100) {
            progress.value = 100
            clearInterval(finish)
            setTimeout(resolve, 300)
          }
        }, 30)
      })
    }

    const submit = async () => {
      if (!isValid.value || submitting.value) return
      submitting.value = true
      startProgress()
      try {
        // Sign out existing user to get a fresh one
        if (auth.currentUser) {
          await auth.signOut()
        }
        // Wait for onAuthStateChanged to create new anonymous user, fallback to manual
        let uid = await new Promise((resolve) => {
          const timeout = setTimeout(async () => {
            const cred = await signInAnonymously(auth)
            resolve(cred.user.uid)
          }, 3000)
          const unsub = auth.onAuthStateChanged((user) => {
            if (user) {
              unsub()
              clearTimeout(timeout)
              resolve(user.uid)
            }
          })
        })
        console.log('[BirthInfo] Fresh user created:', uid)
        const [year, month, date] = form.value.birthDate.split('-').map(Number)
        const [hours, minutes] = form.value.birthTime.split(':').map(Number)

        const userData = {
          uid,
          createdAt: serverTimestamp(),
          properties: {
            name: form.value.name,
            date, month, year, hours, minutes, seconds: 0,
            address: form.value.location.name,
            latitude: form.value.location.lat,
            longitude: form.value.location.lng,
            guessCountry: form.value.location.name?.split(',').pop()?.trim() || '',
            gender: form.value.gender,
            languageStyle: 'balanced',
            updateSystemPrompt: true,
            isWeb: true,
            isPromptTest: true
          }
        }

        console.log('[BirthInfo] Saving to Firestore...')
        await setDoc(doc(db, 'users', uid), userData, { merge: true })

        // Verify user was saved before calling summary
        const verify = await getDoc(doc(db, 'users', uid))
        if (!verify.exists()) {
          throw new Error('User document not found after save')
        }
        console.log('[BirthInfo] User verified in Firestore:', uid)
        localStorage.setItem('userModel', JSON.stringify(userData))

        // Call summary to generate birth chart & system prompt
        console.log('[BirthInfo] Calling summary...')
        const summaryFn = httpsCallable(functions, 'summary')
        await summaryFn({})
        console.log('[BirthInfo] Summary complete')
        emit('summary-complete')

        await finishProgress()
        emit('next', { uid, name: form.value.name })
      } catch (e) {
        clearInterval(progressTimer)
        submitting.value = false
        console.error('[BirthInfo] ERROR:', e.message, e)
        alert('Failed to create user: ' + e.message)
      }
    }

    return { form, submitting, progress, genders, isValid, onLocationSelected, submit }
  }
}
</script>

<style scoped>
/* Layout */
.step-birth { max-width: 480px; margin: 0 auto; padding: 20px; }
.step-birth { text-align: center; }
.step-birth h2 { font-size: 24px; font-weight: 700; color: #fff; margin-bottom: 28px; }

/* Loading */
.loading-screen { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 50vh; gap: 24px; }
.loader-ring { position: relative; width: 120px; height: 120px; }
.loader-ring svg { width: 100%; height: 100%; transform: rotate(-90deg); }
.ring-bg { fill: none; stroke: rgba(255,255,255,0.06); stroke-width: 4; }
.ring-fg { fill: none; stroke: #6c5ce7; stroke-width: 4; stroke-linecap: round; transition: stroke-dasharray 0.3s ease; }
.ring-pct { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: 800; color: #fff; }
.loading-text { font-size: 16px; color: rgba(255,255,255,0.6); }

/* Form - consistent 20px gap between all groups */
.birth-form { display: flex; flex-direction: column; gap: 20px; text-align: left; }
.form-group { display: flex; flex-direction: column; gap: 8px; }
.form-group > label { font-size: 12px; color: #a29bfe; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }

/* Inputs - all same height */
.form-group input[type="text"],
.form-group input[type="date"],
.form-group input[type="time"] {
  height: 48px; box-sizing: border-box;
  padding: 0 16px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15);
  border-radius: 12px; color: #fff; font-size: 15px; color-scheme: dark; font-family: inherit;
}
.form-group input::placeholder { color: rgba(255,255,255,0.3); }
.form-group input:focus { outline: none; border-color: rgba(255,255,255,0.3); }

/* Gender */
.gender-options { display: flex; flex-direction: column; gap: 12px; width: 100%; }
.gender-option { padding: 14px 16px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 12px; color: #fff; font-size: 15px; font-weight: 500; cursor: pointer; transition: all 0.2s; text-align: left; }
.gender-option:hover { background: rgba(255,255,255,0.08); }
.gender-option.active { background: rgba(108,99,255,0.12); border-color: #6c5ce7; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); border: 0; }

/* Button */
.primary-btn { padding: 16px 24px; background: #6c5ce7; border: none; border-radius: 12px; color: #fff; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s; margin-top: 4px; font-family: inherit; }
.primary-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.primary-btn:hover { background: #7c6ff0; box-shadow: 0 6px 20px rgba(108,99,255,0.35); }
</style>
